<template>
  <div class="container-fluid p-0">
    <div class="d-flex align-items-center mt-2">
      <div class="btn-group ms-2">
        <RouterLink :to="TranslationsService.i18nRoute({ name: 'orders' })" class="btn btn-outline-secondary" active-class="">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          {{ $t('global.list') }}
        </RouterLink>
        <button class="btn btn-primary" type="button" :disabled="uiList.length === 0" @click="createOrder()">
          <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
          <font-awesome-icon v-else :icon="['fas', 'cart-shopping']" />
          {{ $t('action.placeOrder') }}
        </button>
        <button class="btn btn-outline-primary" type="button" :disabled="isFillingDemands" @click="fillDemands()">
          <div v-if="isFillingDemands" class="spinner-border spinner-border-sm"></div>
          <font-awesome-icon v-else :icon="['fas', 'wand-magic-sparkles']" />
          {{ $t('action.fillDemands') }}
        </button>
      </div>
      <button class="btn btn-link text-secondary ms-auto me-2 p-0" type="button" :title="$t('page.order-create.help.title')" :aria-label="$t('page.order-create.help.title')" @click="openHelp()">
        <font-awesome-icon :icon="['fas', 'info-circle']" size="lg" />
      </button>
    </div>
    <div class="row mt-2 mx-2 align-items-center">
      <div class="col-auto">
        <label for="scanInput" class="col-form-label">{{ $t('field.masterdata.itemCode') }}</label>
      </div>
      <div class="col-auto">
        <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" :explicit-focus="true" @scanned="barcodeScanned" />
      </div>
    </div>
    <div class="row row-cols-1 row-cols-xxl-2 mt-2 mx-0" v-if="uiList.length > 0">
      <div class="col px-0 table-fix-head" id="content-page-table" :style="fixHeadCalculatedStyle">

        <table class="table table-borderless table-sm align-middle">
          <!-- <thead>
            <tr>
              <th class="mel-bar"></th>
              <th>{{ $t('field.generic.item') }}</th>
              <th>{{ $t('field.generic.quantity') }} / {{ $t('field.generic.supplier') }}</th>
            </tr>
          </thead> -->
          <tbody>
            <template v-for="entry in uiList" :key="entry.item.id">
              <template v-if="entry.isEditing">
                <tr class="border-bottom-0" :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing }">
                  <td class="mel-bar mel-bar-primary"></td>
                  <td colspan="2">
                    <ItemField :item="entry.item" :substitute-item-code="entry.supplierItemCode" />
                  </td>
                  <!-- <td><QuantityField :quantity="entry.quantity" :unit-name="entry.item.baseUnitCode" :show-diff-anim="true" :pad-length="maxQuantityLength" /></td> -->
                </tr>
                <tr :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing, 'border-bottom-0': orderedItemLines(entry.item.id).length > 0 }">
                  <td class="mel-bar mel-bar-primary"></td>
                  <td colspan="2">
                    <div class="d-flex flex-row justify-content-between align-items-center">
                      <button class="btn btn-outline-danger me-2" type="button" @click="removeItem(entry)" :disabled="entry.isAutoManaged">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                      </button>
                      <div class="flex-grow-1">{{ entry.supplier?.name ?? '' }}</div>
                      <span v-if="entry.isMinMax" class="me-2"><RestockPullTypeIcon :restock-pull-type="RESTOCK_PULL_TYPE.minMax" class="text-secondary" /></span>
                      <QuantityField class="w-auto" :quantity="entry.quantity" :unit-name="entry.unitName" :show-diff-anim="true" :show-change-controls="true" :pad-length="maxQuantityLength" :open-input="true" :min-quantity="entry.minQuantity" v-on:update:quantity="updateQuantity(entry, $event)" />
                    </div>
                  </td>
                </tr>
                <tr v-if="acceptedItems(entry.item.id).length > 0" :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing, 'border-bottom-0': unacceptedItems(entry.item.id).length > 0 }">
                  <td class="mel-bar mel-bar-warning">
                  </td>
                  <td colspan="2" class="bg-warning-subtle">
                    <font-awesome-icon :icon="['fas', 'truck']"/>
                    {{$t('page.order-create.quantitiesConfirmedFor', {
                    quantity: sumOrderedQuantities(acceptedItems(entry.item.id)),
                    unitCode: entry.unitName,
                    date: $d(earliestDate(acceptedItems(entry.item.id).map(x => x.deliveryDate!)), 'dateonly')
                  })}}
                  </td>
                </tr>
                <tr v-if="unacceptedItems(entry.item.id).length > 0" :class="{ highlight: entry.highlight, 'row-selected': entry.isEditing }">
                  <td class="mel-bar mel-bar-warning">
                  </td>
                  <td colspan="2" class="bg-warning-subtle">
                    <font-awesome-icon :icon="['fas', 'cart-shopping']"/>
                    {{$t('page.order-create.quantitiesOrderedSince', {
                    quantity: sumOrderedQuantities(unacceptedItems(entry.item.id)),
                    unitCode: entry.unitName,
                    date: $d(earliestDate(unacceptedItems(entry.item.id).map(x => x.orderDate)), 'dateonly')
                  })}}
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr @click="selectRow(entry)" :class="{ highlight: entry.highlight }">
                  <td class="mel-bar"></td>
                  <td>
                    <div class="mel-absolute-warning-container">
                      <div v-if="acceptedItems(entry.item.id).length > 0 || unacceptedItems(entry.item.id).length > 0" class="text-warning fw-bold mel-absolute-warning-background"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" size="2x" opacity="0.4" /></div>
                      <div class="mel-absolute-warning-foreground"><ItemField :item="entry.item" :substitute-item-code="entry.supplierItemCode" /></div>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-row align-items-center justify-content-end">
                      <span v-if="entry.isMinMax" class="me-2"><RestockPullTypeIcon :restock-pull-type="RESTOCK_PULL_TYPE.minMax" class="text-secondary" /></span>
                      <QuantityField class="" :quantity="entry.quantity" :unit-name="entry.unitName" :show-diff-anim="true" :pad-length="maxQuantityLength" />
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>

      </div>
    </div>

    <div class="modal fade" ref="helpModalRef" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
              {{ $t('page.order-create.help.title') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('action.close')"></button>
          </div>
          <div class="modal-body">
            <div v-for="topic in helpTopics" :key="topic.key" class="mb-3">
              <h6 class="mb-1">
                <font-awesome-icon :icon="topic.icon" class="me-1 text-secondary" fixed-width />
                {{ topic.title }}
              </h6>
              <p class="mb-0 text-body-secondary">{{ topic.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import { toast, type ToastOptions } from 'vue3-toastify';
import { router } from '@/services';
import Throttler from "@/services/throttler";
import { useSessionStore, useItemsStore, useStockLevelsStore, useReplacementItemsStore, useSupplierItemsStore, useTradeItemsStore, useItemBlocksStore, useItemSiteSettingsStore, useOrdersStore, useForeignItemsStore } from '@/stores';
import mainHub from '@/hubs/mainHub';
import ScanInput from "@/components/form/ScanInput.vue";
import { ItemField, QuantityField } from "@/components/fields";
import RestockPullTypeIcon from "@/components/masterdata/RestockPullTypeIcon.vue";
import OrderCreateRequest, { DraftOrderLineFields, OrderLineCreateRequest } from '@/models/requests/OrderCreateRequest';
import IGS1 from '@/models/IGS1';
import Item from '@/models/frontend/Item';
import { RESTOCK_PULL_TYPE } from '@/models/Enums';
import Supplier from '@/models/frontend/Supplier';
import TranslationsService from "@/services/translations.ts";

const i18n = useI18n();
const sessionStore = useSessionStore();
const itemsStore = useItemsStore();
const itemSiteSettingStore = useItemSiteSettingsStore();
const tradeItemsStore = useTradeItemsStore();
const itemBlocksStore = useItemBlocksStore();
const stockLevelsStore = useStockLevelsStore();
const replacementItemsStore = useReplacementItemsStore();
const supplierItemsStore = useSupplierItemsStore();
const foreignItemsStore = useForeignItemsStore();
const ordersStore = useOrdersStore();

const draftSaveThrottler = new Throttler();
const throttledSaveDraft = draftSaveThrottler.throttle(
  async () => {
    await saveDraft();
  },
  [],
  10000,
  { leading: true, trailing: true }
);


const { selectedSiteId } = storeToRefs(sessionStore);
const { data: orders } = storeToRefs(ordersStore);

const isSaving = ref(false);
const isFillingDemands = ref(false);
const saveToastId = 'save';
const draftToastId = 'draft';
const fillDemandsToastId = 'fill-demands';
const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const helpModalRef = ref<HTMLElement | null>(null);
const offsetTop = ref(0);

const helpTopics = computed(() => [
  {
    key: 'placeOrder',
    icon: ['fas', 'cart-shopping'],
    title: i18n.t('page.order-create.help.topics.placeOrder.title'),
    description: i18n.t('page.order-create.help.topics.placeOrder.description'),
  },
  {
    key: 'fillDemands',
    icon: ['fas', 'wand-magic-sparkles'],
    title: i18n.t('page.order-create.help.topics.fillDemands.title'),
    description: i18n.t('page.order-create.help.topics.fillDemands.description'),
  },
  {
    key: 'autoOrder',
    icon: ['fas', 'clock'],
    title: i18n.t('page.order-create.help.topics.autoOrder.title'),
    description: i18n.t('page.order-create.help.topics.autoOrder.description'),
  },
]);

const openHelp = () => {
  if (helpModalRef.value) {
    Modal.getOrCreateInstance(helpModalRef.value).show();
  }
};

// Store the callback reference for cleanup
const orderDraftChangedCallback = async (siteId: number) => {
  if (siteId === selectedSiteId.value) {
    await loadDraftData();
  }
};

class ItemOrderLine {
  constructor(
  public itemId: number,
  public quantity: number,
  public deliveryDate: Date | undefined,
  public orderDate: Date,
  ) {}
}

class SelectableItem {
  public highlight = false;
  public isEditing: boolean = true;
  public isAutoManaged: boolean = false;
  public isMinMax: boolean = false;
  public minQuantity: number = 1;
  public managedFloor: number | null = null;
  constructor(
    public createTimestamp: number,
    public item: Item,
    public unitCode: string,
    public quantity: number,
    public supplier: Supplier,
    public factor: number,
    public baseUnitCode: string,
    public foreignItemCode?: string,
    public supplierItemCode?: string
  ) { }

  get unitName() {
    if (this.unitCode === this.baseUnitCode) {
      return this.unitCode;
    }
    return `${this.unitCode} à ${this.factor} ${this.baseUnitCode}`;
  }
}

const orderedItems = ref([] as SelectableItem[]);

const uiList = computed(() => [...orderedItems.value].sort((a, b) =>
  b.createTimestamp - a.createTimestamp || a.item.name.localeCompare(b.item.name)
));

const maxQuantityLength = computed(() => {
  return uiList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const orderQuantities = computed(() => {
  return orders.value
    .map(x => x.lines.map(l => new ItemOrderLine(l.item.id, l.quantity, x.deliveryDate, new Date(x.audit.createdAt))))
    .flat();
});

const orderedItemLines = (itemId: number): ItemOrderLine[] => {
  return orderQuantities.value.filter(x => x.itemId == itemId);
};
const acceptedItems = (itemId: number): ItemOrderLine[] => {
  return orderedItemLines(itemId).filter(x => x.deliveryDate);
};
const unacceptedItems = (itemId: number): ItemOrderLine[] => {
  return orderedItemLines(itemId).filter(x => !x.deliveryDate);
};

const sumOrderedQuantities = (itemOrderLines: ItemOrderLine[]) :number => {
  return itemOrderLines.reduce((acc, item) => acc + item.quantity, 0);
};

const earliestDate = (dates: Date[]) : Date => {
  return new Date(Math.min(...dates.map(x => new Date(x).getTime())));
}

onMounted(async () => {
  scanInput.value?.focus();
  await loadDraftData();
  await ordersStore.fetchAll();
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;

  // Listen for OrderDraftChanged events
  mainHub.onOrderDraftChanged(orderDraftChangedCallback);
});

const loadDraftData = async () => {
  const draft = await ordersStore.loadDraft();
  if (draft) {
    // Create a map of draft items by itemId for efficient lookup
    const draftMap = new Map(draft.map(line => [line.itemId, line]));
    
    // Update existing items and mark which ones are still in the draft
    const itemsToKeep = new Set<number>();
    
    orderedItems.value.forEach(orderedItem => {
      const draftLine = draftMap.get(orderedItem.item.id);
      if (draftLine) {
        orderedItem.quantity = draftLine.quantity;
        orderedItem.unitCode = draftLine.unitCode;
        orderedItem.managedFloor = draftLine.managedFloor;
        // The floor reflects the current demand-based minimum. It can move up or down as level config changes.
        orderedItem.minQuantity = draftLine.managedFloor ?? 1;
        itemsToKeep.add(orderedItem.item.id);
      }
    });
    
    // Remove items that are no longer in the draft
    orderedItems.value = orderedItems.value.filter(item => itemsToKeep.has(item.item.id));
    
    // Add new items that are in the draft but not in orderedItems
    const loadingTimestamp = Date.now();
    draft.forEach(line => {
      if (!itemsToKeep.has(line.itemId)) {
        const item = itemsStore.getById(line.itemId);
        const constructedItem = constructItem(item, true, loadingTimestamp);
        if (constructedItem) {
          constructedItem.isEditing = false;
          constructedItem.quantity = line.quantity;
          constructedItem.unitCode = line.unitCode;
          constructedItem.managedFloor = line.managedFloor;
          constructedItem.minQuantity = line.managedFloor ?? 1;
          orderedItems.value.push(constructedItem);
        }
      }
    });
  } else {
    // If no draft exists, clear the array
    orderedItems.value = [];
  }
};


onBeforeUnmount(async () => {
  if (helpModalRef.value) {
    Modal.getInstance(helpModalRef.value)?.dispose();
  }
  // Unregister the OrderDraftChanged event listener
  mainHub.offOrderDraftChanged(orderDraftChangedCallback);
  await saveDraft();
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const updateQuantity = (item: SelectableItem, quantity: number) => {
  item.quantity = Math.max(quantity, item.minQuantity);
  throttledSaveDraft();
};

const selectRow = (selectedItem: SelectableItem) => {
  orderedItems.value.forEach(item => {
    item.isEditing = item === selectedItem;
  });
};

const removeItem = (item: SelectableItem) => {
  const index = orderedItems.value.indexOf(item);
  if (index > -1) {
    orderedItems.value.splice(index, 1);
    throttledSaveDraft();
  }
};

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    var tradeItem = tradeItemsStore.getByCode(contentCode);
    if (tradeItem) {
      const item = itemsStore.getById(tradeItem.itemId);
      const constructedItem = constructItem(item);
      if (constructedItem) {
        addItemToList(constructedItem);
        return;
      }
    }
    if (gs1?.itemCode) {
      const item = itemsStore.getByCode(gs1.itemCode);
      const constructedItem = constructItem(item);
      if (constructedItem) {
        addItemToList(constructedItem);
        return;
      }
    }
  }

  // if not matched already, try get item by code
  const item = itemsStore.getByCode(barcode);
  const constructedItem = constructItem(item);
  if (constructedItem) {
    addItemToList(constructedItem);
    return;
  }

  // if not matched already, try get item block by code
  var itemBlock = itemBlocksStore.getByCode(barcode);
  if (itemBlock) {
    itemBlock.partItems.forEach(pi => {
      const item = itemsStore.getById(pi.itemId);
      const constructedItem = constructItem(item);
      if (constructedItem) {
        addItemToList(constructedItem);
      }
    });
  }
};

const constructItem = (item: Item, forDraft: boolean = false, loadingTimestamp: number = Date.now()) => {
  if (!item) return;

  const supplierItem = supplierItemsStore.getByItemId(item.id);
  if (!supplierItem) {
    toast.warn(i18n.t('error.noSupplierFoundForItem', { itemId: item.id, tenantCode: item.tenant.code, itemCode: item.code }), { autoClose: 10000, theme: 'colored' });
    console.warn(`No supplier found for item #${item.id} (${item.tenant.code}/${item.code})`);
    return; // not expected to happen
  }

  const itemSupplier = supplierItem.supplier;
  if (!itemSupplier) {
    toast.warn(i18n.t('error.noSupplierItemFoundForItem', { itemId: item.id, tenantCode: item.tenant.code, itemCode: item.code, supplierId: supplierItem.supplier.id, supplierItemId: supplierItem.id, supplierItemCode: supplierItem.code }), { autoClose: 10000, theme: 'colored' });
    console.warn(`No supplier item found for item #${item.id} (${item.tenant.code}/${item.code}) with supplier #${supplierItem.supplier.id}, supplier item ${supplierItem.id} (${supplierItem.code})`);
    return; // not expected to happen
  }

  let rootItem = item;
  const baseItemId = replacementItemsStore.getBaseItem(item.id)
  if (baseItemId) {
    const baseItem = itemsStore.getById(baseItemId);
    if (!baseItem) {
      toast.warn(i18n.t('error.baseItemNotFoundForItem', { baseItemId: baseItemId, itemId: item.id, tenantCode: item.tenant.code, itemCode: item.code }), { autoClose: 10000, theme: 'colored' });
      console.warn(`Base item #${baseItemId} not found for item #${item.id} (${item.tenant.code}/${item.code})`);
      return; // not expected to happen
    }
    rootItem = baseItem;
  }

  const itemSiteSetting = itemSiteSettingStore.getByItemId(rootItem.id);
  const effectivePullType = itemSiteSetting?.restockPullType ?? item.restockPullType;
  const canOrderManually = itemSiteSetting?.canOrderManually ?? false;
  // MinMax items may appear in the draft (or be re-scanned while present) even without canOrderManually.
  const isMinMax = effectivePullType === RESTOCK_PULL_TYPE.minMax;
  const isAlreadyInDraft = orderedItems.value.some(x => x.item.id === rootItem.id);
  const allowAsAutoManaged = isMinMax && (forDraft || isAlreadyInDraft);

  if (effectivePullType !== RESTOCK_PULL_TYPE.onDemand && !canOrderManually && !allowAsAutoManaged) {
    toast.warn(i18n.t('error.itemNotManagedByDemand', { itemId: rootItem.id, tenantCode: rootItem.tenant.code, itemCode: rootItem.code }), { autoClose: 10000, theme: 'colored' });
    console.debug(`Item #${rootItem.id} (${rootItem.tenant.code}/${rootItem.code}) is not managed by demand`);
    return; // skip items that are not managed by demand
  }

  const stockLevel = stockLevelsStore.getByItemId(rootItem.id);
  if (stockLevel && !canOrderManually && !allowAsAutoManaged) {
    toast.warn(i18n.t('error.itemManagedByStockLevels', { itemId: rootItem.id, tenantCode: rootItem.tenant.code, itemCode: rootItem.code }), { autoClose: 10000, theme: 'colored' });
    console.debug(`Item #${rootItem.id} (${rootItem.tenant.code}/${rootItem.code}) is managed by stock levels`);
    return; // skip items that are managed by stock levels
  }

  const foreignItem = foreignItemsStore.getByItemId(rootItem.id);
  const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(rootItem.id, supplierItem.orderUnitCode);
  const selectableItem = tradeItem === undefined
    ? new SelectableItem(loadingTimestamp, item, supplierItem.orderUnitCode, supplierItem.proposedOrderQuantity ?? 1, itemSupplier, 1, supplierItem.orderUnitCode, foreignItem?.code, supplierItem.code)
    : new SelectableItem(loadingTimestamp, item, supplierItem.orderUnitCode, supplierItem.proposedOrderQuantity ?? 1, itemSupplier, tradeItem.factor, item.baseUnitCode, foreignItem?.code, supplierItem.code);
  selectableItem.isAutoManaged = allowAsAutoManaged;
  selectableItem.isMinMax = isMinMax;
  return selectableItem;
}

const addItemToList = (constructedItem: SelectableItem) => {
  const existingItem = orderedItems.value.find((item) => item.item.id === constructedItem.item.id);
  if (existingItem) {
    existingItem.quantity += 1;
  } else {
    constructedItem.highlight = true;
    selectRow(constructedItem);
    orderedItems.value.push(constructedItem);
    throttledSaveDraft();
    setTimeout(() => {
      constructedItem.highlight = false;
    }, 3000);
  }

  scanInput.value?.focus();
};

const fillDemands = async () => {
  isFillingDemands.value = true;
  toast.loading(i18n.t('status.loadingEllipsis'), {
    toastId: fillDemandsToastId,
    autoClose: false,
    theme: toast.THEME.COLORED,
  } as ToastOptions);
  try {
    const changed = await ordersStore.fillDraftWithDemands();
    if (changed) {
      await loadDraftData();
    }
    toast.remove(fillDemandsToastId);
  } catch (error: any) {
    toast.update(fillDemandsToastId, {
      render: error?.message ?? String(error),
      type: toast.TYPE.ERROR,
      isLoading: false,
      autoClose: 10000,
      closeOnClick: true,
    } as ToastOptions);
  } finally {
    isFillingDemands.value = false;
  }
};

const createOrder = async () => {
  if (uiList.value.length === 0) {
    toast.warn(i18n.t('error.nothingToSave'), { autoClose: 10000, theme: 'colored' });
    return;
  }
  isSaving.value = true;
  toast.loading(i18n.t('page.order-create.orderSaving'), {
    toastId: saveToastId,
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  const orderLines = uiList.value
    .map((item) => (new OrderLineCreateRequest(item.item.id, item.quantity)))
    .sort((a, b) => a.itemId - b.itemId);
  const orderRequest = new OrderCreateRequest(selectedSiteId.value, undefined, orderLines);
  await ordersStore.create(orderRequest, onCreateOrderApiFulfilled, onApiRejected);
};

const saveDraft = async () => {
  toast.loading(i18n.t('page.order-create.orderSaving'), {
    toastId: draftToastId,
    autoClose: false,
    theme: toast.THEME.COLORED,

    transition: toast.TRANSITIONS.ZOOM,
    position: toast.POSITION.TOP_RIGHT,
  } as ToastOptions);
  const orderLines = uiList.value
    .map((entry) => (new DraftOrderLineFields(entry.item.id, entry.quantity, entry.unitCode, entry.managedFloor)))
    .sort((a, b) => a.itemId - b.itemId);
  await ordersStore.saveDraft(orderLines, onSaveDraftApiFulfilled, onSaveDraftApiRejected);
};

function onSaveDraftApiFulfilled(): void {
  toast.remove(draftToastId);
}

function onSaveDraftApiRejected(): void | PromiseLike<void> {
  toast.remove(draftToastId);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onCreateOrderApiFulfilled(): void {
  orderedItems.value = [];
  isSaving.value = false;
  toast.update(saveToastId, {
    render: i18n.t('page.order-create.ordersSaved'),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);
  router.push({ name: 'orders' });
}

function onApiRejected(error: any): void | PromiseLike<void> {
  isSaving.value = false;
  toast.update(saveToastId, {
    render: i18n.t('page.order-create.orderSaveError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
}
</script>

<style scoped type="scss">
input[type=number] {
  text-align: right;
}

th {
  font-weight: bold;
}

tr {
  border-bottom: 1px dashed #ccc;

  &:last-child {
    border-bottom: none;
  }
}

th,
td {
  padding-left: .5rem;
  padding-right: .5rem;
}

@keyframes highlight-bg {
  from {
    background-color: var(--bs-melodi-bg-subtle);
  }
  to {
    background-color: var(--mel-row-filled-bg);
  }
}

.highlight {
  animation: highlight-bg 3s;
}

.row-selected > td {
  background-color: var(--mel-row-filled-bg);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent !important;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg) !important;
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary-bg-subtle) !important;
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success) !important;
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning) !important;
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger) !important;
}

.mel-absolute-warning-container {
  position: relative; /* Acts as the reference point */
  overflow: hidden;
}
.mel-absolute-warning-background {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  z-index: 0;
}
.mel-absolute-warning-foreground {
  position: relative; /* Keeps it above the background */
  z-index: 1;
}
</style>
